import { compact, isEmpty, map } from "lodash/fp";

import { get, httpDelete, patch, post } from "@readai/common/api";
import {
  ApiCreateTeamRequestBody,
  ApiEnterpriseTeam,
  ApiEnterpriseTeamUser,
  DeleteTeamUserRequestBody,
  DeleteTeamUsersRequestBody,
  EnterpriseTeam,
  EnterpriseTeamSettings,
  EnterpriseTeamWithSettings,
  EnterpriseUserBody,
  EnterpriseWorkspaceUser,
  PatchEnterpriseUserBody,
} from "@readai/common/types/workspaceTypes";
import { populateUser } from "@readai/common/utils";
import {
  isPromiseFulfilled,
  isPromiseRejected,
} from "@readai/common/utils/apiUtils";

export const createTeam = async (
  workspaceId: string,
  body: ApiCreateTeamRequestBody,
): Promise<EnterpriseTeam> => {
  return post(`/enterprise/workspaces/${workspaceId}/teams`, body, {
    withCreds: true,
  });
};

export const getTeam = async (
  workspaceId: string,
  teamId: string,
): Promise<EnterpriseTeam> => {
  const team: ApiEnterpriseTeam = await get(
    `/enterprise/workspaces/${workspaceId}/teams/${teamId}`,
    { withCreds: true },
  );

  return {
    ...team,
    // The API can return null settings. Protect against
    // that, so we don't need null checks
    settings: team.settings ?? {},
    users: map(
      (u) => ({ ...populateUser<ApiEnterpriseTeamUser>(u), type: "user" }),
      team.users,
    ),
  };
};

export const createTeamUser = async (
  workspaceId: string,
  teamId: string,
  user: EnterpriseUserBody,
): Promise<void> =>
  post(`/enterprise/workspaces/${workspaceId}/teams/${teamId}/users`, user, {
    withCreds: true,
  });

export const addUsersToTeam = async ({
  workspaceId,
  teamId,
  users,
}: {
  workspaceId: string;
  teamId: string;
  users: EnterpriseWorkspaceUser[];
}): Promise<EnterpriseWorkspaceUser[]> => {
  if (isEmpty(users)) return [];

  const results = await Promise.allSettled(
    users.map((u) =>
      createTeamUser(workspaceId, teamId, {
        userId: u.userId,
        role: "member",
      }),
    ),
  );

  const failedUsers = results.map((result, i) =>
    isPromiseFulfilled(result) ? null : users[i],
  );

  return compact(failedUsers);
};

export const patchTeamUser = async (
  workspaceId: string,
  teamId: string,
  user: PatchEnterpriseUserBody,
): Promise<void> => {
  const { userId, role } = user;
  return patch(
    `/enterprise/workspaces/${workspaceId}/teams/${teamId}/users/${userId}`,
    { role },
    { withCreds: true },
  );
};

export const deleteTeamUser = async ({
  workspaceId,
  teamId,
  userId,
}: DeleteTeamUserRequestBody): Promise<void> =>
  httpDelete(
    `/enterprise/workspaces/${workspaceId}/teams/${teamId}/users/${userId}`,
    { withCreds: true },
  );

/**
 * Returns a list of the user ids that failed to be successfully removed
 * */
export const deleteTeamUsers = async ({
  workspaceId,
  teamId,
  userIds,
}: DeleteTeamUsersRequestBody): Promise<string[]> => {
  const results = await Promise.allSettled(
    userIds.map((userId) => deleteTeamUser({ workspaceId, teamId, userId })),
  );

  const erroredUsers = results.map((result, i) =>
    isPromiseRejected(result) ? userIds[i] : null,
  );

  return compact(erroredUsers);
};

export const patchTeam = async (
  workspaceId: string,
  {
    id: teamId,
    ...body
  }: Omit<Partial<EnterpriseTeamWithSettings>, "id" | "settings"> & {
    id: string;
    settings?: Partial<EnterpriseTeamSettings>;
  },
): Promise<void> =>
  patch(`/enterprise/workspaces/${workspaceId}/teams/${teamId}`, body, {
    withCreds: true,
  });

export const deleteTeam = async (
  workspaceId: string,
  teamId: string,
): Promise<void> =>
  httpDelete(`/enterprise/workspaces/${workspaceId}/teams/${teamId}`, {
    withCreds: true,
  });
